<script setup>
import { ref, nextTick } from 'vue'
import { useRouter } from 'vue-router';
const phone = ref('')
const phoneError = ref({
    error: false,
    message: ""
});

const otpDigits = Array(5).fill('').map(() => ref(''))
const otpInputs = Array(5).fill(null).map(() => ref(null))

const step = ref(1)
const showLoadingModal = ref(false)

const resendCooldown = ref(60)
const resendDisabled = ref(false)
const showResentMessage = ref(false)



import { requestOtpForPasswordRecovery, verifyOtpForPasswordRecovery } from '@/api/admin';
import { useAdminStore } from '@/stores/admin';
import { useI18n } from 'vue-i18n';



const { t } = useI18n();

const validatePhone = () => {
    if (!phone.value) {
        phoneError.value.error = true;
        phoneError.value.message = t('login.phoneRequired');
        return false
    }
    if (!/^0\d{9}$/.test(phone.value)) {
        phoneError.value.error = true;
        phoneError.value.message = t('login.phoneInvalid'); return false
    }
    phoneError.value.error = false
    phoneError.value.message = ''
    return true
}

const adminStore = useAdminStore();
const sendCode = () => {
    if (!validatePhone()) return

    showLoadingModal.value = true
    setTimeout(async () => {
        try {
            if (!isCanceled.value) {

                adminStore.phone = phone.value;

                const response = await requestOtpForPasswordRecovery(phone.value);
                showLoadingModal.value = false;
                console.log('response :>> ', response);

                if (response.data.success) {
                    step.value = 2;
                    await nextTick(() => otpInputs[0]?.focus());
                } else {
                    phoneError.value.error = true;
                    phoneError.value.message = 'Numero téléphone invalid .';
                }
            }

            isCanceled.value = false;

        } catch (error) {
            showLoadingModal.value = false;
            phoneError.value.error = true;
            phoneError.value.message = t('login.phoneInvalidGeneric');

            console.error('Erreur OTP:', error);
            // alert('Impossible d’envoyer le code OTP. Veuillez réessayer.');
        }
    }, 2000);

}


const isCanceled = ref(false);
const cancelSend = () => {
    showLoadingModal.value = false
    isCanceled.value = true;
}

const handleOtpInput = (e, index) => {
    const value = e.target.value.replace(/\D/g, '')
    if (value) {
        otpDigits[index].value = value[0]
        if (index < otpInputs.length - 1) {
            otpInputs[index + 1]?.focus()
        } else {
          
            const code = otpDigits.map(d => d.value).join('')
            if (code.length === otpDigits.length) {
                verifyCode(code) 
            }
        }
    } else {
        otpDigits[index].value = ''
    }
}

const handleOtpKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otpDigits[index].value && index > 0) {
        otpInputs[index - 1]?.focus()
    }
}

const resendCode = async () => {
    let response = await requestOtpForPasswordRecovery(phone.value);
    if (response.data.success) {
        otpDigits.forEach(d => d.value = '')
        nextTick(() => otpInputs[0]?.focus())

        resendDisabled.value = true
        resendCooldown.value = 60
        showResentMessage.value = true



        const interval = setInterval(() => {
            resendCooldown.value--
            if (resendCooldown.value <= 0) {
                clearInterval(interval)
                resendDisabled.value = false
                showResentMessage.value = false
            }
        }, 1000)
    }
}

const codeError = ref({
    error: false,

});

const router = useRouter();


const showVerifyingModal = ref(false);

const verifyCode = async () => {
    const code = otpDigits.map(d => d.value).join('');


    if (code.length !== 5) {
        codeError.value.error = true;
        return;
    }

    showVerifyingModal.value = true;
    codeError.value.error = false;

    try {
        const response = await verifyOtpForPasswordRecovery({
            phone: phone.value,
            code
        });

        console.log('response :>> ', response);

        showVerifyingModal.value = false;


        if (response.data.success) {

            let user = response.data.user;
            let token = response.data.token;
            let subscription = response.data.subscription;
            adminStore.setAdmin({ token, user, subscription });
            if (adminStore.$state.type == 'subscriber') {
                router.push('edit');
            } else {
                router.push('/admin/recover-password');
            }
        } else {

            codeError.value.error = true;
            console.log('response :>> ', response);
        }
    } catch (error) {

        showVerifyingModal.value = false;
        codeError.value.error = true;
        console.error('OTP verification failed:', error);


    }
};



</script>

<template>
    <div class="w-full h-full flex items-center justify-center px-5 lg:px-0">
        <div
            class="h-auto bg-white border border-gray-200 shadow-lg rounded-xl px-6 py-6 w-full lg:w-fit space-y-3 flex flex-col justify-center">

            <!-- Title and subtitle -->
            <div :class="(step == 1 ? 'h-2/3' : 'h-1/2') + ' w-full flex flex-col justify-center items-center'">
                <div class="text-center">
                    <h1 class="text-2xl font-bold text-primary">{{ $t('recover_password.title') }}</h1>
                    <p class="text-gray-500 text-sm mt-2">{{ $t('recover_password.subtitle') }}</p>
                </div>

                <!-- Phone input -->
                <div class="w-full mt-3">
                    <label class="block text-sm font-medium text-gray-700 mb-1"
                        :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'">
                        {{ $t('recover_password.phoneLabel') }}
                    </label>
                    <input type="tel" v-model="phone" :disabled="step === 2" :class="[
                        'w-full border rounded-lg px-3 py-2 outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-100 transition duration-200',
                        phoneError.error ? 'border-red-400' : 'border-gray-300'
                    ]" :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'" />
                    <p v-if="phoneError.error" class="text-red-500 text-xs mt-1"
                        :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'">
                        {{ phoneError.message }}
                    </p>
                </div>
            </div>

            <!-- Step 1: Send Code -->
            <div :class="(step == 1 ? 'h-1/3' : 'h-1/2') + ' w-full'">
                <div v-if="step === 1" class="pt-2">
                    <button @click="sendCode"
                        class="w-full bg-primary text-white rounded-lg py-2 hover:bg-secondary transition duration-200">
                        {{ $t('recover_password.sendCode') }}
                    </button>


                </div>


                <div v-if="step === 2" class="space-y-4">
                    <div class="text-gray-600 text-center text-sm py-2">
                        {{ $t('recover_password.otpSent') }}
                    </div>

                    <!-- OTP inputs -->
                    <div class="flex justify-center space-x-3 w-full py-2 h-16">
                        <template v-for="(digit, index) in otpDigits" :key="index">
                            <input type="text" maxlength="1" inputmode="numeric" pattern="[0-9]*"
                                :ref="el => otpInputs[index] = el" v-model="digit.value"
                                @input="(e) => handleOtpInput(e, index)" @keydown="(e) => handleOtpKeyDown(e, index)"
                                :class="[
                                    codeError.error ? 'border border-red-300' : 'border border-gray-300',
                                    'w-12 h-full rounded-lg text-center text-lg focus:ring-2 focus:ring-primary outline-none'
                                ]" />
                        </template>
                    </div>

                    <!-- Error -->
                    <div v-if="codeError.error" class="text-sm text-red-500 text-center font-medium">
                        {{ $t('recover_password.codeError') }}
                    </div>

                    <!-- Resend code -->
                    <div v-if="showResentMessage" class="text-primary text-xs text-center">
                        {{ $t('recover_password.resendCode') }}
                    </div>
                    <button @click="resendCode" :disabled="resendDisabled"
                        class="w-full text-primary font-medium text-sm hover:underline disabled:text-gray-400 disabled:cursor-not-allowed">
                        <span v-if="!resendDisabled">{{ $t('recover_password.resendCode') }}</span>
                        <span v-else>{{ $t('recover_password.resendIn', { seconds: resendCooldown }) }}</span>
                    </button>

                    <!-- Confirm button -->
                    <button @click="verifyCode"
                        class="w-full bg-primary text-white rounded-lg py-2 hover:bg-secondary transition duration-200">
                        {{ $t('recover_password.confirm') }}
                    </button>
                </div>
            </div>
        </div>



        <transition>
            <div v-if="showVerifyingModal"
                class="fixed w-full h-full inset-0 bg-black/30 flex justify-center items-center z-50">
                <div class="w-full h-1/2 flex flex-col justify-center p-3 items-center space-y-5">
                    <div class="flex justify-center space-x-2 mt-3">
                        <span class="w-3 h-3 bg-white shadow-lg rounded-full animate-bounce-wave delay-0"></span>
                        <span class="w-3 h-3 bg-white shadow-lg rounded-full animate-bounce-wave delay-150"></span>
                        <span class="w-3 h-3 bg-white shadow-lg rounded-full animate-bounce-wave delay-300"></span>
                    </div>
                </div>
            </div>
        </transition>

        <div v-if="showLoadingModal" class="absolute inset-0 bg-black/30 flex justify-center items-center z-50">
            <div class="bg-white rounded-xl px-6 lg:w-1/4 py-5 text-center flex flex-col justify-evenly items-center">
                <div class="w-full h-1/2 flex flex-col justify-end">
                    <p class="text-gray-600 text-sm">
                        {{ $t('recover_password.sendingCode') }}
                    </p>
                    <div class="w-full mt-3">
                        <input type="tel" v-model="phone" :disabled="true"
                            class="w-full border rounded-lg px-3 py-2 outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-100 disabled:text-center transition duration-200 text-gray-500" />
                    </div>
                </div>

                <div class="w-full h-1/2 flex flex-col justify-end p-3 items-center space-y-5">
                    <div class="flex justify-center space-x-2 mt-3">
                        <span class="w-3 h-3 bg-primary rounded-full animate-bounce-wave delay-0"></span>
                        <span class="w-3 h-3 bg-primary rounded-full animate-bounce-wave delay-150"></span>
                        <span class="w-3 h-3 bg-primary rounded-full animate-bounce-wave delay-300"></span>
                    </div>

                    <button @click="cancelSend"
                        class="w-fit text-white bg-red-400 rounded-lg px-3 py-2 hover:underline text-sm font-medium">
                        {{ $t('recover_password.cancel') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>


<style scoped>
@keyframes bounce-wave {

    0%,
    80%,
    100% {
        transform: translateY(0);
    }

    40% {
        transform: translateY(-8px);
    }
}

.animate-bounce-wave {
    animation: bounce-wave 1.2s infinite ease-in-out;
}

.delay-0 {
    animation-delay: 0s;
}

.delay-150 {
    animation-delay: 0.15s;
}

.delay-300 {
    animation-delay: 0.3s;
}

/* Remove arrows for Chrome, Safari, Edge, and Opera */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

/* Remove arrows for Firefox */
input[type="number"] {
    -moz-appearance: textfield;
}
</style>
